import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./admin/Admin.css";
import * as XLSX from "xlsx";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FiDownload,
  FiEdit2,
  FiEye,
  FiEyeOff,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiUser,
  FiX,
} from "react-icons/fi";

const toolbarButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  whiteSpace: "nowrap",
  borderRadius: "12px",
  fontWeight: 600,
  padding: "10px 16px",
  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.08)",
  transition: "none",
  cursor: "pointer",
};

const actionButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  whiteSpace: "nowrap",
  flexShrink: 0,
  minWidth: "84px",
  borderRadius: "999px",
  fontWeight: 600,
  padding: "6px 12px",
  transition: "none",
  cursor: "pointer",
};

const actionGroupStyle = {
  display: "flex",
  gap: "8px",
  flexWrap: "nowrap",
  alignItems: "center",
  minWidth: "max-content",
};

const buttonSpinnerStyle = {
  width: "14px",
  height: "14px",
  borderWidth: "0.15em",
  flexShrink: 0,
};

const ManageProduct = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loadingDisabledProducts, setLoadingDisabledProducts] = useState(false);
  const [showingDisabledProducts, setShowingDisabledProducts] = useState(false);
  const [deletingProducts, setDeletingProducts] = useState({});
  const [togglingProducts, setTogglingProducts] = useState({});
  const [formData, setFormData] = useState({
    MaSP: "",
    Ten_SP: "",
    DVT_SP: "",
    MaLoaiSP: 3,
    MaNLSP: "",
    DonGia: 0,
    DonGia1: 0,
    DonGia2: 0,
    GiaMua: 0,
    TenUser: "Admin",
    ThuTuHienThiSP: 0,
    VAT_SP: 0,
  });

  const API_BASE_URL = "https://doticom.vn:3000";

  // Memoized format currency function
  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(amount || 0);
  }, []);

  // Memoized category lookup
  const getCategoryName = useCallback(
    (maNLSP) => {
      const category = categories.find((cat) => cat.MaNLSP === maNLSP);
      return category ? category.TenLoaiSP : maNLSP;
    },
    [categories],
  );

  const getProductActiveValue = useCallback((product) => {
    const rawValue = product.ActSP ?? product.Act_SP;

    if (typeof rawValue === "boolean") {
      return rawValue ? 1 : 0;
    }

    const parsedValue = Number(rawValue);
    return Number.isNaN(parsedValue) ? 1 : parsedValue === 0 ? 0 : 1;
  }, []);

  const getResponseErrorMessage = useCallback(async (response, fallback) => {
    try {
      const errorData = await response.json();
      return errorData.message || errorData.error || fallback;
    } catch (jsonError) {
      try {
        const errorText = await response.text();
        return errorText || fallback;
      } catch (textError) {
        return fallback;
      }
    }
  }, []);

  // Optimized fetch functions with error handling
  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const isHcmAdmin = localStorage.getItem("HCM_ADMIN") === "true";
      const endpoint = isHcmAdmin
        ? `${API_BASE_URL}/api/san-pham-all-hcm`
        : `${API_BASE_URL}/api/san-pham-all`;
      const response = await fetch(endpoint, {
        signal: AbortController ? new AbortController().signal : undefined,
      });
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
        setShowingDisabledProducts(false);
      } else {
        console.error("Failed to fetch products");
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error fetching products:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  const fetchDisabledProducts = useCallback(async () => {
    try {
      setLoading(true);
      setLoadingDisabledProducts(true);
      const isHcmAdmin = localStorage.getItem("HCM_ADMIN") === "true";
      const endpoint = isHcmAdmin
        ? `${API_BASE_URL}/api/san-pham-disable-hcm`
        : `${API_BASE_URL}/api/san-pham-disable`;

      const response = await fetch(endpoint);
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
        setShowingDisabledProducts(true);
      } else {
        const errorMessage = await getResponseErrorMessage(
          response,
          "Không thể tải danh sách sản phẩm đang ẩn",
        );
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error fetching disabled products:", error);
      toast.error("Có lỗi xảy ra khi tải danh sách sản phẩm đang ẩn");
    } finally {
      setLoading(false);
      setLoadingDisabledProducts(false);
    }
  }, [API_BASE_URL, getResponseErrorMessage]);

  const fetchCategories = useCallback(async () => {
    try {
      const HCM_ADMIN = localStorage.getItem("HCM_ADMIN");
      const endpoint =
        HCM_ADMIN === "true"
          ? `${API_BASE_URL}/categories-hcm`
          : `${API_BASE_URL}/categories`;
      const response = await fetch(endpoint);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, [API_BASE_URL]);

  // Debounced search function
  const debouncedSearch = useCallback(
    (keyword, group) => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }

      const timeout = setTimeout(async () => {
        if (!keyword.trim()) {
          fetchProducts();
          return;
        }

        try {
          setLoading(true);
          setShowingDisabledProducts(false);
          const url = new URL(`${API_BASE_URL}/api/san-pham-search`);
          url.searchParams.append("keyword", keyword);
          if (group) {
            url.searchParams.append("maNLSP", group);
          }

          const response = await fetch(url);
          if (response.ok) {
            const data = await response.json();
            setProducts(data);
          }
        } catch (error) {
          console.error("Error searching products:", error);
        } finally {
          setLoading(false);
        }
      }, 300);

      setSearchTimeout(timeout);
    },
    [API_BASE_URL, fetchProducts, searchTimeout],
  );

  // Immediate search function for button click
  const searchProducts = useCallback(async () => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (!searchKeyword.trim()) {
      fetchProducts();
      return;
    }

    try {
      setLoading(true);
      setShowingDisabledProducts(false);
      const url = new URL(`${API_BASE_URL}/api/san-pham-search`);
      url.searchParams.append("keyword", searchKeyword);
      if (selectedGroup) {
        url.searchParams.append("maNLSP", selectedGroup);
      }

      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error("Error searching products:", error);
    } finally {
      setLoading(false);
    }
  }, [
    API_BASE_URL,
    searchKeyword,
    selectedGroup,
    fetchProducts,
    searchTimeout,
  ]);

  // Filter by group
  const filterByGroup = useCallback(
    async (maNLSP) => {
      if (!maNLSP) {
        fetchProducts();
        return;
      }

      try {
        setLoading(true);
        setShowingDisabledProducts(false);
        const response = await fetch(
          `${API_BASE_URL}/api/san-pham-theo-nhom/${maNLSP}`,
        );
        if (response.ok) {
          const data = await response.json();
          setProducts(data);
        }
      } catch (error) {
        console.error("Error filtering products:", error);
      } finally {
        setLoading(false);
      }
    },
    [API_BASE_URL, fetchProducts],
  );

  const resetForm = useCallback(() => {
    setFormData({
      MaSP: "",
      Ten_SP: "",
      DVT_SP: "",
      MaLoaiSP: 3,
      MaNLSP: "",
      DonGia: 0,
      DonGia1: 0,
      DonGia2: 0,
      GiaMua: 0,
      TenUser: "Admin",
      ThuTuHienThiSP: 0,
      VAT_SP: 0,
    });
  }, []);

  // Optimized save function
  const saveProduct = useCallback(async () => {
    try {
      setSaving(true);

      const url = editingProduct
        ? `${API_BASE_URL}/api/san-pham-update/${editingProduct.MaSP}`
        : `${API_BASE_URL}/api/san-pham-create`;

      const method = editingProduct ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setShowModal(false);
        setEditingProduct(null);
        resetForm();
        await fetchProducts();
        toast.success(
          editingProduct
            ? "Cập nhật sản phẩm thành công!"
            : "Thêm sản phẩm thành công!",
        );
        return;
      }

      const errorMessage = await getResponseErrorMessage(
        response,
        "Không thể lưu sản phẩm",
      );
      toast.error(errorMessage);
    } catch (error) {
      console.error("Error saving product:", error);
      toast.error("Có lỗi xảy ra khi lưu sản phẩm");
    } finally {
      setSaving(false);
    }
  }, [
    API_BASE_URL,
    editingProduct,
    formData,
    fetchProducts,
    getResponseErrorMessage,
    resetForm,
  ]);

  // Add export to Excel function
  const exportToExcel = useCallback(async () => {
    try {
      setExporting(true);

      // Prepare data for export
      const exportData = products.map((product, index) => ({
        STT: index + 1,
        "Mã SP": product.MaSP,
        "Tên sản phẩm": product.Ten_SP,
        "Đơn vị tính": product.DVT_SP,
        "Nhóm SP": getCategoryName(product.MaNLSP),
        "Giá mua": product.GiaMua || 0,
        "Giá bán": product.DonGia || 0,
        "Giá nhân viên": product.DonGia1 || 0,
        "Giá Grab": product.DonGia2 || 0,
        "VAT (%)": product.VAT_SP || 0,
        "Thứ tự hiển thị": product.ThuTuHienThiSP || 0,
        "Người tạo": product.TenUser || "",
      }));

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      const colWidths = [
        { wch: 5 }, // STT
        { wch: 12 }, // Mã SP
        { wch: 30 }, // Tên sản phẩm
        { wch: 12 }, // Đơn vị tính
        { wch: 20 }, // Nhóm SP
        { wch: 15 }, // Giá mua
        { wch: 15 }, // Giá bán
        { wch: 15 }, // Giá nhân viên
        { wch: 15 }, // Giá Grab
        { wch: 10 }, // VAT
        { wch: 12 }, // Thứ tự
        { wch: 15 }, // Người tạo
      ];
      ws["!cols"] = colWidths;

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Danh sách sản phẩm");

      // Generate filename with current date
      const today = new Date();
      const dateStr = `${today.getDate()}-${
        today.getMonth() + 1
      }-${today.getFullYear()}`;
      const filename = `DanhSachSanPham_${dateStr}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);

      toast.success("Xuất file Excel thành công!");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Có lỗi xảy ra khi xuất file Excel");
    } finally {
      setExporting(false);
    }
  }, [products, getCategoryName]);

  // Handle search input change with debouncing
  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      setSearchKeyword(value);
      debouncedSearch(value, selectedGroup);
    },
    [selectedGroup, debouncedSearch],
  );

  // Handle group filter change
  const handleGroupChange = useCallback(
    (e) => {
      const value = e.target.value;
      setSelectedGroup(value);
      filterByGroup(value);
    },
    [filterByGroup],
  );

  // Memoized form handlers
  const handleInputChange = useCallback((e) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? parseFloat(value) || 0 : value,
    }));
  }, []);

  // Memoized modal handlers
  const openAddModal = useCallback(() => {
    setEditingProduct(null);
    resetForm();
    setShowModal(true);
  }, [resetForm]);

  const handleDisabledProductsClick = useCallback(() => {
    setSearchKeyword("");
    setSelectedGroup("");

    if (showingDisabledProducts) {
      fetchProducts();
      return;
    }

    fetchDisabledProducts();
  }, [fetchDisabledProducts, fetchProducts, showingDisabledProducts]);

  const openEditModal = useCallback((product) => {
    setEditingProduct(product);
    setFormData({
      MaSP: product.MaSP,
      Ten_SP: product.Ten_SP,
      DVT_SP: product.DVT_SP,
      MaLoaiSP: product.MaLoaiSP,
      MaNLSP: product.MaNLSP,
      DonGia: product.DonGia,
      DonGia1: product.DonGia1,
      DonGia2: product.DonGia2,
      GiaMua: product.GiaMua || 0,
      TenUser: product.TenUser,
      ThuTuHienThiSP: product.ThuTuHienThiSP,
      VAT_SP: product.VAT_SP,
    });
    setShowModal(true);
  }, []);

  const deleteProduct = useCallback(
    async (maSP) => {
      if (!window.confirm("Bạn có chắc chắn muốn xóa sản phẩm này?")) {
        return;
      }

      try {
        setDeletingProducts((prev) => ({
          ...prev,
          [maSP]: true,
        }));

        const response = await fetch(
          `${API_BASE_URL}/api/san-pham-delete/${maSP}`,
          {
            method: "DELETE",
          },
        );

        if (response.ok) {
          await fetchProducts();
          toast.success("Xóa sản phẩm thành công!");
          return;
        }

        const errorMessage = await getResponseErrorMessage(
          response,
          "Không thể xóa sản phẩm",
        );
        toast.error(errorMessage);
      } catch (error) {
        console.error("Error deleting product:", error);
        toast.error("Có lỗi xảy ra khi xóa sản phẩm");
      } finally {
        setDeletingProducts((prev) => {
          const nextState = { ...prev };
          delete nextState[maSP];
          return nextState;
        });
      }
    },
    [API_BASE_URL, fetchProducts, getResponseErrorMessage],
  );

  /*
  useCallback(async (product) => {
    const currentActSP = getProductActiveValue(product);
    const nextActSP = currentActSP === 1 ? 0 : 1;
    const actionLabel = nextActSP === 1 ? 'hiá»‡n' : 'áº©n';

    try {
      setTogglingProducts(prev => ({
        ...prev,
        [product.MaSP]: true
      }));

      const response = await fetch(`${API_BASE_URL}/api/san-pham/update-actsp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          MaSP: product.MaSP,
          ActSP: nextActSP
        }),
      });

      if (response.ok) {
        setProducts(prevProducts =>
          prevProducts.map(item =>
            item.MaSP === product.MaSP
              ? { ...item, ActSP: nextActSP, Act_SP: nextActSP }
              : item
          )
        );
        alert(`ÄÃ£ ${actionLabel} sáº£n pháº©m thÃ nh cÃ´ng!`);
        return;
      }

      let errorMessage = 'KhÃ´ng thá»ƒ cáº­p nháº­t tráº¡ng thÃ¡i sáº£n pháº©m';

      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch (parseError) {
        const errorText = await response.text();
        if (errorText) {
          errorMessage = errorText;
        }
      }

      alert(`Lá»—i: ${errorMessage}`);
    } catch (error) {
      console.error('Error toggling product visibility:', error);
      alert('CÃ³ lá»—i xáº£y ra khi cáº­p nháº­t tráº¡ng thÃ¡i sáº£n pháº©m');
    } finally {
      setTogglingProducts(prev => {
        const nextState = { ...prev };
        delete nextState[product.MaSP];
        return nextState;
      });
    }
  }, [API_BASE_URL, getProductActiveValue]);
  */

  const toggleProductVisibility = useCallback(
    async (product) => {
      const currentActSP = getProductActiveValue(product);
      const nextActSP = currentActSP === 1 ? 0 : 1;
      const actionLabel = nextActSP === 1 ? "hiện" : "ẩn";

      try {
        setTogglingProducts((prev) => ({
          ...prev,
          [product.MaSP]: true,
        }));

        const response = await fetch(
          `${API_BASE_URL}/api/san-pham/update-actsp`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              MaSP: product.MaSP,
              ActSP: nextActSP,
            }),
          },
        );

        if (response.ok) {
          setProducts((prevProducts) =>
            prevProducts.map((item) =>
              item.MaSP === product.MaSP
                ? { ...item, ActSP: nextActSP, Act_SP: nextActSP }
                : item,
            ),
          );
          toast.success(`Đã ${actionLabel} sản phẩm thành công!`);
          return;
        }

        const errorMessage = await getResponseErrorMessage(
          response,
          "Không thể cập nhật trạng thái sản phẩm",
        );
        toast.error(errorMessage);
      } catch (error) {
        console.error("Error toggling product visibility:", error);
        toast.error("Có lỗi xảy ra khi cập nhật trạng thái sản phẩm");
      } finally {
        setTogglingProducts((prev) => {
          const nextState = { ...prev };
          delete nextState[product.MaSP];
          return nextState;
        });
      }
    },
    [API_BASE_URL, getProductActiveValue, getResponseErrorMessage],
  );

  // Memoized table rows to prevent unnecessary re-renders
  const productRows = useMemo(() => {
    return products.map((product) => (
      <tr key={product.MaIDSP}>
        <td>
          <strong>{product.MaSP}</strong>
        </td>
        <td>{product.Ten_SP}</td>
        <td>{product.DVT_SP}</td>
        <td>
          <span className="badge bg-secondary">
            {getCategoryName(product.MaNLSP)}
          </span>
        </td>
        <td>{formatCurrency(product.GiaMua)}</td>
        <td>{formatCurrency(product.DonGia)}</td>
        <td>{formatCurrency(product.DonGia1)}</td>
        <td>{formatCurrency(product.DonGia2)}</td>
        <td>{product.VAT_SP}%</td>
        <td>{product.ThuTuHienThiSP}</td>
        <td>
          <div style={actionGroupStyle}>
            <button
              onClick={() => openEditModal(product)}
              className="btn btn-outline-warning btn-sm"
              title="Chỉnh sửa"
              style={actionButtonStyle}
              disabled={
                Boolean(togglingProducts[product.MaSP]) ||
                Boolean(deletingProducts[product.MaSP])
              }
            >
              <FiEdit2 size={14} />
              <span>Sửa</span>
            </button>
            <button
              onClick={() => toggleProductVisibility(product)}
              className={`btn btn-sm ${
                getProductActiveValue(product) === 1
                  ? "btn-outline-secondary"
                  : "btn-outline-success"
              }`}
              title={
                getProductActiveValue(product) === 1
                  ? "Ẩn sản phẩm"
                  : "Hiện sản phẩm"
              }
              disabled={
                Boolean(togglingProducts[product.MaSP]) ||
                Boolean(deletingProducts[product.MaSP])
              }
              style={actionButtonStyle}
            >
              {Boolean(togglingProducts[product.MaSP]) ? (
                <span
                  className="spinner-border spinner-border-sm"
                  aria-hidden="true"
                  style={buttonSpinnerStyle}
                />
              ) : getProductActiveValue(product) === 1 ? (
                <FiEyeOff size={14} />
              ) : (
                <FiEye size={14} />
              )}
              <span>
                {Boolean(togglingProducts[product.MaSP])
                  ? "Đang xử lý"
                  : getProductActiveValue(product) === 1
                  ? "Ẩn"
                  : "Hiện"}
              </span>
            </button>
            <button
              onClick={() => deleteProduct(product.MaSP)}
              className="btn btn-outline-danger btn-sm"
              title="Xóa"
              style={actionButtonStyle}
              disabled={
                Boolean(deletingProducts[product.MaSP]) ||
                Boolean(togglingProducts[product.MaSP])
              }
            >
              {Boolean(deletingProducts[product.MaSP]) ? (
                <span
                  className="spinner-border spinner-border-sm"
                  aria-hidden="true"
                  style={buttonSpinnerStyle}
                />
              ) : (
                <FiTrash2 size={14} />
              )}
              <span>{Boolean(deletingProducts[product.MaSP]) ? "Đang xóa" : "Xóa"}</span>
            </button>
          </div>
        </td>
      </tr>
    ));
  }, [
    deletingProducts,
    products,
    getCategoryName,
    formatCurrency,
    openEditModal,
    deleteProduct,
    getProductActiveValue,
    toggleProductVisibility,
    togglingProducts,
  ]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [fetchProducts, fetchCategories]);

  return (
    <div className="admin-layout">
      <div className="admin-main">
        {/* Header */}
        <div className="admin-header">
          <div className="header-left">
            <h1>Quản Lý Sản Phẩm</h1>
            <p>Quản lý thông tin sản phẩm trong hệ thống</p>
          </div>
          <div className="user-info">
            <div className="user-avatar">
              <FiUser size={18} />
            </div>
            <span className="user-name">{localStorage.getItem("TenUser")}</span>
          </div>
        </div>

        {/* Content */}
        <div className="content-section">
          <div className="card">
            <div className="card-header">
              <div className="shift-actions">
                <div
                  style={{ display: "flex", gap: "12px", alignItems: "center" }}
                >
                  <input
                    type="text"
                    placeholder="Tìm kiếm sản phẩm..."
                    value={searchKeyword}
                    onChange={handleSearchChange}
                    className="form-control"
                    style={{ width: "250px" }}
                  />
                  <button
                    onClick={searchProducts}
                    className="btn btn-primary"
                    style={toolbarButtonStyle}
                    disabled={loading}
                  >
                    {loading ? (
                      <span
                        className="spinner-border spinner-border-sm"
                        aria-hidden="true"
                        style={buttonSpinnerStyle}
                      />
                    ) : (
                      <FiSearch size={16} />
                    )}
                    <span>{loading ? "Đang tải" : "Tìm kiếm"}</span>
                  </button>
                  <select
                    value={selectedGroup}
                    onChange={handleGroupChange}
                    className="form-select"
                    style={{ width: "200px" }}
                  >
                    <option value="">Tất cả nhóm</option>
                    {categories.map((category) => (
                      <option key={category.MaNLSP} value={category.MaNLSP}>
                        {category.TenLoaiSP}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ display: "flex", gap: "12px" }}>
                  <button
                    onClick={handleDisabledProductsClick}
                    className={`btn ${showingDisabledProducts ? "btn-outline-dark" : "btn-outline-secondary"}`}
                    style={toolbarButtonStyle}
                    disabled={loadingDisabledProducts}
                  >
                    {loadingDisabledProducts ? (
                      <span
                        className="spinner-border spinner-border-sm"
                        aria-hidden="true"
                        style={buttonSpinnerStyle}
                      />
                    ) : showingDisabledProducts ? (
                      <FiEye size={16} />
                    ) : (
                      <FiEyeOff size={16} />
                    )}
                    <span>
                      {loadingDisabledProducts
                        ? "Đang tải sản phẩm ẩn"
                        : showingDisabledProducts
                          ? "Xem sản phẩm đang hiển thị"
                          : "Xem sản phẩm đang ẩn"}
                    </span>
                  </button>
                  <button
                    onClick={exportToExcel}
                    className="btn btn-success"
                    style={toolbarButtonStyle}
                    disabled={exporting}
                  >
                    {exporting ? (
                      <span
                        className="spinner-border spinner-border-sm"
                        aria-hidden="true"
                        style={buttonSpinnerStyle}
                      />
                    ) : (
                      <FiDownload size={16} />
                    )}
                    <span>{exporting ? "Đang xuất" : "Xuất Excel"}</span>
                  </button>
                  <button
                    onClick={openAddModal}
                    className="btn btn-primary"
                    style={toolbarButtonStyle}
                  >
                    <FiPlus size={16} />
                    <span>Thêm sản phẩm</span>
                  </button>
                </div>
              </div>
            </div>

            <div className="card-body">
              {loading ? (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <div className="spinner-border text-primary"></div>
                  <p style={{ marginTop: "12px" }}>Đang tải dữ liệu...</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="shift-table">
                    <thead>
                      <tr>
                        <th>Mã SP</th>
                        <th>Tên sản phẩm</th>
                        <th>Đơn vị tính</th>
                        <th>Nhóm SP</th>
                        <th>Giá mua</th>
                        <th>Giá bán</th>
                        <th>Giá nhân viên</th>
                        <th>Giá Grab</th>
                        <th>VAT (%)</th>
                        <th>Thứ tự</th>
                        <th>Thao tác</th>
                      </tr>
                    </thead>
                    <tbody>{productRows}</tbody>
                  </table>

                  {products.length === 0 && (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "40px",
                        color: "#6c757d",
                      }}
                    >
                      Không có sản phẩm nào được tìm thấy
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>
                {editingProduct ? "Chỉnh sửa sản phẩm" : "Thêm sản phẩm mới"}
              </h3>
              <button
                className="modal-close"
                onClick={() => setShowModal(false)}
                type="button"
                aria-label="Đóng"
                disabled={saving}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FiX size={18} />
              </button>
            </div>

            <div className="modal-body">
              <div className="shift-form">
                <div className="form-group">
                  <label>Mã sản phẩm *</label>
                  <input
                    type="text"
                    name="MaSP"
                    value={formData.MaSP}
                    onChange={handleInputChange}
                    className="form-control"
                    disabled={!!editingProduct}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Tên sản phẩm *</label>
                  <input
                    type="text"
                    name="Ten_SP"
                    value={formData.Ten_SP}
                    onChange={handleInputChange}
                    className="form-control"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Đơn vị tính *</label>
                  <input
                    type="text"
                    name="DVT_SP"
                    value={formData.DVT_SP}
                    onChange={handleInputChange}
                    className="form-control"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Nhóm loại sản phẩm *</label>
                  <select
                    name="MaNLSP"
                    value={formData.MaNLSP}
                    onChange={handleInputChange}
                    className="form-control"
                    required
                  >
                    <option value="">Chọn nhóm sản phẩm</option>
                    {categories.map((category) => (
                      <option key={category.MaNLSP} value={category.MaNLSP}>
                        {category.TenLoaiSP}
                      </option>
                    ))}
                  </select>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "8px",
                  }}
                >
                  <div className="form-group">
                    <label>Giá mua</label>
                    <input
                      type="number"
                      name="GiaMua"
                      value={formData.GiaMua}
                      onChange={handleInputChange}
                      className="form-control"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div className="form-group">
                    <label>Giá bán</label>
                    <input
                      type="number"
                      name="DonGia"
                      value={formData.DonGia}
                      onChange={handleInputChange}
                      className="form-control"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "8px",
                  }}
                >
                  <div className="form-group">
                    <label>Giá nhân viên</label>
                    <input
                      type="number"
                      name="DonGia1"
                      value={formData.DonGia1}
                      onChange={handleInputChange}
                      className="form-control"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div className="form-group">
                    <label>Giá Grab</label>
                    <input
                      type="number"
                      name="DonGia2"
                      value={formData.DonGia2}
                      onChange={handleInputChange}
                      className="form-control"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "8px",
                  }}
                >
                  <div className="form-group">
                    <label>VAT (%)</label>
                    <input
                      type="number"
                      name="VAT_SP"
                      value={formData.VAT_SP}
                      onChange={handleInputChange}
                      className="form-control"
                      min="0"
                      max="100"
                      step="0.01"
                    />
                  </div>

                  <div className="form-group">
                    <label>Thứ tự hiển thị</label>
                    <input
                      type="number"
                      name="ThuTuHienThiSP"
                      value={formData.ThuTuHienThiSP}
                      onChange={handleInputChange}
                      className="form-control"
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Người tạo</label>
                  <input
                    type="text"
                    name="TenUser"
                    value={formData.TenUser}
                    onChange={handleInputChange}
                    className="form-control"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowModal(false)}
                className="btn btn-secondary"
                disabled={saving}
              >
                Hủy
              </button>
              <button
                onClick={saveProduct}
                className="btn btn-primary"
                disabled={saving}
                style={toolbarButtonStyle}
              >
                {saving ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    aria-hidden="true"
                    style={buttonSpinnerStyle}
                  />
                ) : (
                  <FiEdit2 size={16} />
                )}
                <span>{saving ? "Đang lưu" : editingProduct ? "Cập nhật" : "Thêm mới"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={2500}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
        theme="light"
      />
    </div>
  );
};

export default ManageProduct;
